/**
 * DragSnapScroll — 水平拖拽滚动 + 松手/惯性后左侧对齐（类似 Swiper）
 *
 * - 鼠标：自定义拖拽滚动（不影响触屏原生滑动）
 * - 触屏：保留原生 overflow 滑动，在 scrollend / 惯性结束后 snap
 *
 * Usage:
 *   const scroller = new theme.modules.DragSnapScroll(element, {
 *     getSnapStep: () => 280,
 *     // 或 snapSelector: '.slide',
 *     onScroll: () => {},
 *   });
 *   scroller.bind();
 *   scroller.scrollByStep(1);
 *   scroller.destroy();
 *
 * Optional custom element:
 *   <drag-snap-scroll data-snap-selector=".item">...</drag-snap-scroll>
 */
(() => {
  const DRAG_POINTER_TYPES = new Set(['mouse']);

  /**
   * @param {number} t
   */
  function easeOutCubic(t) {
    return 1 - (1 - t) ** 3;
  }

  class DragSnapScroll {
    /**
     * @param {HTMLElement} element 可横向滚动的容器
     * @param {{
     *   axis?: 'x' | 'y';
     *   dragPointerTypes?: string[];
     *   snapSelector?: string | null;
     *   getSnapStep?: (() => number) | null;
     *   getSnapPositions?: (() => number[]) | null;
     *   snapDuration?: number;
     *   dragThreshold?: number;
     *   flickVelocity?: number;
     *   noDragSelector?: string | null;
     *   onScroll?: ((scrollLeft: number) => void) | null;
     *   onSnapStart?: ((scrollLeft: number) => void) | null;
     *   onSnapEnd?: ((scrollLeft: number) => void) | null;
     * }} [options]
     */
    constructor(element, options = {}) {
      this.el = element;
      this.options = {
        axis: 'x',
        dragPointerTypes: [...DRAG_POINTER_TYPES],
        snapSelector: null,
        getSnapStep: null,
        getSnapPositions: null,
        snapDuration: 280,
        dragThreshold: 4,
        flickVelocity: 0.45,
        // 链接/按钮上仍允许拖滚动；真正拖动后由 click capture 抑制点击
        // 表单控件/label 不启动拖拽，避免抢走勾选与聚焦
        noDragSelector: 'input, textarea, select, option, label, [data-no-drag-scroll]',
        snapOnResize: true,
        resizeDebounce: 100,
        onScroll: null,
        onSnapStart: null,
        onSnapEnd: null,
        ...options,
      };

      this._bound = false;
      this._dragging = false;
      this._moved = false;
      this._pointerCaptured = false;
      this._pointerId = null;
      this._startX = 0;
      this._startY = 0;
      this._startScroll = 0;
      this._lastX = 0;
      this._lastY = 0;
      this._lastT = 0;
      this._velocity = 0;
      this._raf = 0;
      this._scrollEndTimer = 0;
      this._settleRaf = 0;
      this._resizeTimer = 0;
      this._lastSnapIndex = 0;
      this._suppressClick = false;
      this._touchActive = false;

      this._onPointerDown = this.onPointerDown.bind(this);
      this._onPointerMove = this.onPointerMove.bind(this);
      this._onPointerUp = this.onPointerUp.bind(this);
      this._onClickCapture = this.onClickCapture.bind(this);
      this._onNativeDragStart = this.onNativeDragStart.bind(this);
      this._onScroll = this.onScroll.bind(this);
      this._onScrollEnd = this.onScrollEnd.bind(this);
      this._onTouchStart = this.onTouchStart.bind(this);
      this._onTouchEnd = this.onTouchEnd.bind(this);
      this._onLostPointerCapture = this.onLostPointerCapture.bind(this);
      this._onResize = this.onResize.bind(this);
    }

    get isHorizontal() {
      return this.options.axis !== 'y';
    }

    get scrollProp() {
      return this.isHorizontal ? 'scrollLeft' : 'scrollTop';
    }

    get maxScroll() {
      if (!this.el) return 0;
      return this.isHorizontal
        ? Math.max(0, this.el.scrollWidth - this.el.clientWidth)
        : Math.max(0, this.el.scrollHeight - this.el.clientHeight);
    }

    bind() {
      if (!this.el || this._bound) return this;
      this._bound = true;
      this.el.classList.add('drag-snap-scroll');
      this.el.addEventListener('pointerdown', this._onPointerDown);
      this.el.addEventListener('click', this._onClickCapture, true);
      this.el.addEventListener('dragstart', this._onNativeDragStart, true);
      this.el.addEventListener('scroll', this._onScroll, {passive: true});
      this.el.addEventListener('scrollend', this._onScrollEnd);
      this.el.addEventListener('touchstart', this._onTouchStart, {passive: true});
      this.el.addEventListener('touchend', this._onTouchEnd, {passive: true});
      this.el.addEventListener('touchcancel', this._onTouchEnd, {passive: true});
      this.el.addEventListener('lostpointercapture', this._onLostPointerCapture);
      if (this.options.snapOnResize) {
        window.addEventListener('resize', this._onResize);
      }
      return this;
    }

    destroy() {
      if (!this.el || !this._bound) return;
      this._bound = false;
      this.cancelAnimation();
      this.clearScrollEndTimer();
      this.cancelSettle();
      this.clearResizeTimer();
      this._touchActive = false;
      this.endDrag(false);
      this.el.classList.remove('drag-snap-scroll', 'is-dragging');
      this.el.removeEventListener('pointerdown', this._onPointerDown);
      this.el.removeEventListener('pointermove', this._onPointerMove);
      this.el.removeEventListener('pointerup', this._onPointerUp);
      this.el.removeEventListener('pointercancel', this._onPointerUp);
      this.el.removeEventListener('click', this._onClickCapture, true);
      this.el.removeEventListener('dragstart', this._onNativeDragStart, true);
      this.el.removeEventListener('scroll', this._onScroll);
      this.el.removeEventListener('scrollend', this._onScrollEnd);
      this.el.removeEventListener('touchstart', this._onTouchStart);
      this.el.removeEventListener('touchend', this._onTouchEnd);
      this.el.removeEventListener('touchcancel', this._onTouchEnd);
      this.el.removeEventListener('lostpointercapture', this._onLostPointerCapture);
      window.removeEventListener('resize', this._onResize);
    }

    /**
     * @param {number} direction -1 | 1
     * @param {{ behavior?: ScrollBehavior }} [opts]
     */
    scrollByStep(direction, opts = {}) {
      const positions = this.getSnapPositions();
      if (!positions.length) {
        const step = this.resolveSnapStep() || Math.max(this.el.clientWidth * 0.8, 200);
        this.animateTo(this.el[this.scrollProp] + direction * step, opts);
        return;
      }

      const current = this.el[this.scrollProp];
      let target = current;
      if (direction > 0) {
        target = positions.find((pos) => pos > current + 2) ?? positions[positions.length - 1];
      } else {
        for (let i = positions.length - 1; i >= 0; i -= 1) {
          if (positions[i] < current - 2) {
            target = positions[i];
            break;
          }
        }
        if (target === current) target = positions[0];
      }
      this.animateTo(target, opts);
    }

    snapToNearest(opts = {}) {
      const target = this.pickSnapTarget(this.el[this.scrollProp], 0);
      this.animateTo(target, opts);
    }

    /**
     * 尺寸变化后按当前 snap 索引重新对齐（列宽变化时保持所在页）
     * @param {{ behavior?: ScrollBehavior }} [opts]
     */
    refresh(opts = {}) {
      if (!this.el || this._dragging || this._touchActive) return;
      const positions = this.getSnapPositions();
      if (!positions.length) return;

      const index = Math.max(
        0,
        Math.min(this._lastSnapIndex, positions.length - 1),
      );
      // 若尚未记录过有效索引，则按当前位置就近对齐
      const current = this.el[this.scrollProp];
      let target = positions[index];
      if (this._lastSnapIndex <= 0 && current > 2) {
        target = this.pickSnapTarget(current, 0);
      }
      this.animateTo(target, {behavior: opts.behavior || 'auto', ...opts});
    }

    /**
     * @param {number} scrollLeft
     * @returns {number}
     */
    indexOfNearest(scrollLeft) {
      const positions = this.getSnapPositions();
      if (!positions.length) return 0;
      let nearestIndex = 0;
      let nearestDist = Math.abs(scrollLeft - positions[0]);
      for (let i = 1; i < positions.length; i += 1) {
        const dist = Math.abs(scrollLeft - positions[i]);
        if (dist < nearestDist) {
          nearestIndex = i;
          nearestDist = dist;
        }
      }
      return nearestIndex;
    }

    /**
     * @returns {number[]}
     */
    getSnapPositions() {
      if (typeof this.options.getSnapPositions === 'function') {
        return this.normalizePositions(this.options.getSnapPositions());
      }

      if (this.options.snapSelector) {
        const nodes = [...this.el.querySelectorAll(this.options.snapSelector)];
        if (nodes.length) {
          const positions = nodes.map((node) =>
            this.isHorizontal ? node.offsetLeft : node.offsetTop,
          );
          return this.normalizePositions(positions);
        }
      }

      const step = this.resolveSnapStep();
      if (!step) return this.normalizePositions([0, this.maxScroll]);

      /** @type {number[]} */
      const positions = [];
      const max = this.maxScroll;
      for (let x = 0; x < max; x += step) {
        positions.push(x);
      }
      positions.push(max);
      return this.normalizePositions(positions);
    }

    /**
     * @returns {number}
     */
    resolveSnapStep() {
      if (typeof this.options.getSnapStep === 'function') {
        return Math.max(0, Number(this.options.getSnapStep()) || 0);
      }
      return 0;
    }

    /**
     * @param {number[]} positions
     * @returns {number[]}
     */
    normalizePositions(positions) {
      const max = this.maxScroll;
      const unique = [...new Set((positions || []).map((n) => Math.max(0, Math.min(max, Number(n) || 0))))];
      unique.sort((a, b) => a - b);
      if (!unique.length) return [0];
      if (unique[0] !== 0) unique.unshift(0);
      if (unique[unique.length - 1] !== max) unique.push(max);
      return unique;
    }

    /**
     * @param {number} current
     * @param {number} velocity px/ms，水平向右为正
     */
    pickSnapTarget(current, velocity = 0) {
      const positions = this.getSnapPositions();
      if (positions.length <= 1) return positions[0] || 0;

      let nearest = positions[0];
      let nearestDist = Math.abs(current - nearest);
      for (let i = 1; i < positions.length; i += 1) {
        const dist = Math.abs(current - positions[i]);
        if (dist < nearestDist) {
          nearest = positions[i];
          nearestDist = dist;
        }
      }

      const flick = this.options.flickVelocity;
      if (Math.abs(velocity) >= flick) {
        if (velocity > 0) {
          return positions.find((pos) => pos > current + 2) ?? positions[positions.length - 1];
        }
        for (let i = positions.length - 1; i >= 0; i -= 1) {
          if (positions[i] < current - 2) return positions[i];
        }
        return positions[0];
      }

      return nearest;
    }

    /**
     * @param {number} target
     * @param {{ behavior?: ScrollBehavior; duration?: number }} [opts]
     */
    animateTo(target, opts = {}) {
      if (!this.el) return;
      this.cancelAnimation();
      this.clearScrollEndTimer();
      this.cancelSettle();

      const max = this.maxScroll;
      const to = Math.max(0, Math.min(max, target));
      const from = this.el[this.scrollProp];
      if (Math.abs(to - from) < 1) {
        this.el[this.scrollProp] = to;
        this._lastSnapIndex = this.indexOfNearest(to);
        this.options.onSnapEnd?.(to);
        return;
      }

      const behavior = opts.behavior || 'smooth';
      if (behavior === 'auto') {
        this.el[this.scrollProp] = to;
        this._lastSnapIndex = this.indexOfNearest(to);
        this.options.onSnapEnd?.(to);
        return;
      }

      this.options.onSnapStart?.(to);
      const duration = opts.duration ?? this.options.snapDuration;
      const start = performance.now();
      this._lastSnapIndex = this.indexOfNearest(to);

      const step = (now) => {
        this._raf = 0;
        // 触屏再次按下时立即让出控制权，避免与原生滚动打架
        if (!this.el || this._touchActive || this._dragging) return;

        const t = Math.min(1, (now - start) / duration);
        this.el[this.scrollProp] = from + (to - from) * easeOutCubic(t);
        if (t < 1) {
          this._raf = requestAnimationFrame(step);
        } else {
          this.el[this.scrollProp] = to;
          this._lastSnapIndex = this.indexOfNearest(to);
          this.options.onSnapEnd?.(to);
        }
      };

      this._raf = requestAnimationFrame(step);
    }

    /**
     * 打断进行中的吸附/惯性等待（新的触屏手势开始时）
     */
    interruptSnap() {
      this.cancelAnimation();
      this.clearScrollEndTimer();
      this.cancelSettle();
    }

    cancelAnimation() {
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = 0;
      }
    }

    clearScrollEndTimer() {
      if (this._scrollEndTimer) {
        clearTimeout(this._scrollEndTimer);
        this._scrollEndTimer = 0;
      }
    }

    clearResizeTimer() {
      if (this._resizeTimer) {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = 0;
      }
    }

    onResize() {
      if (!this.options.snapOnResize || this._dragging || this._touchActive) return;
      this.clearResizeTimer();
      this._resizeTimer = window.setTimeout(() => {
        this._resizeTimer = 0;
        // 等布局/列宽 CSS 生效后再对齐
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.refresh({behavior: 'auto'}));
        });
      }, this.options.resizeDebounce);
    }

    cancelSettle() {
      if (this._settleRaf) {
        cancelAnimationFrame(this._settleRaf);
        this._settleRaf = 0;
      }
    }

    /**
     * 等待惯性滚动停稳后再 snap（触屏 / 不支持 scrollend 时）
     */
    settleAndSnap() {
      if (!this.el || this._dragging || this._touchActive || this._raf) return;
      this.cancelSettle();
      let last = this.el[this.scrollProp];
      let stable = 0;

      const tick = () => {
        this._settleRaf = 0;
        if (!this.el || this._dragging || this._touchActive || this._raf) return;
        const now = this.el[this.scrollProp];
        if (Math.abs(now - last) < 1) {
          stable += 1;
          if (stable >= 3) {
            this.snapToNearest();
            return;
          }
        } else {
          stable = 0;
          last = now;
        }
        this._settleRaf = requestAnimationFrame(tick);
      };

      this._settleRaf = requestAnimationFrame(tick);
    }

    /**
     * @param {PointerEvent} event
     */
    onPointerDown(event) {
      if (!this.el) return;

      // 触屏用原生滚动；按下时打断未完成的吸附，避免打架
      if (event.pointerType === 'touch' || event.pointerType === 'pen') {
        this._touchActive = true;
        this.interruptSnap();
        return;
      }

      if (!this.options.dragPointerTypes.includes(event.pointerType)) return;
      if (event.button != null && event.button !== 0) return;
      const noDragSelector = this.options.noDragSelector;
      if (noDragSelector && event.target?.closest?.(noDragSelector)) {
        return;
      }

      this.interruptSnap();
      this._dragging = true;
      this._moved = false;
      this._pointerCaptured = false;
      this._suppressClick = false;
      this._pointerId = event.pointerId;
      this._startX = event.clientX;
      this._startY = event.clientY;
      this._startScroll = this.el[this.scrollProp];
      this._lastX = event.clientX;
      this._lastY = event.clientY;
      this._lastT = performance.now();
      this._velocity = 0;

      // 等超过拖拽阈值再 capture，否则会抢走 a/button 的点击
      this.el.addEventListener('pointermove', this._onPointerMove);
      this.el.addEventListener('pointerup', this._onPointerUp);
      this.el.addEventListener('pointercancel', this._onPointerUp);
    }

    /**
     * 阻止图片/链接的浏览器原生拖拽，避免与横向拖滚动冲突
     * @param {DragEvent} event
     */
    onNativeDragStart(event) {
      if (event.target?.closest?.('img, a, picture')) {
        event.preventDefault();
      }
    }

    /**
     * @param {PointerEvent} event
     */
    onPointerMove(event) {
      if (!this._dragging || event.pointerId !== this._pointerId) return;

      const dx = event.clientX - this._startX;
      const dy = event.clientY - this._startY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (!this._moved) {
        if (absX < this.options.dragThreshold && absY < this.options.dragThreshold) return;
        // 更偏纵向时放弃水平拖拽，交给页面/弹窗竖向滚动
        if (this.isHorizontal && absY > absX) {
          this.endDrag(false);
          return;
        }
        this._moved = true;
        this._suppressClick = true;
        this.el.classList.add('is-dragging');
        if (!this._pointerCaptured) {
          this._pointerCaptured = true;
          this.el.setPointerCapture?.(event.pointerId);
        }
      }

      event.preventDefault();
      const now = performance.now();
      const dt = Math.max(now - this._lastT, 1);
      const delta = this.isHorizontal ? event.clientX - this._lastX : event.clientY - this._lastY;
      this._velocity = delta / dt;
      this._lastX = event.clientX;
      this._lastY = event.clientY;
      this._lastT = now;

      const next = this.isHorizontal
        ? this._startScroll - dx
        : this._startScroll - dy;
      this.el[this.scrollProp] = Math.max(0, Math.min(this.maxScroll, next));
    }

    /**
     * @param {PointerEvent} event
     */
    onPointerUp(event) {
      if (event.pointerId !== this._pointerId) return;
      const shouldSnap = this._dragging && this._moved;
      const velocity = this.isHorizontal ? -this._velocity : -this._velocity;
      this.endDrag(shouldSnap, velocity);
    }

    onLostPointerCapture() {
      if (!this._dragging) return;
      const shouldSnap = this._moved;
      const velocity = -this._velocity;
      this.endDrag(shouldSnap, velocity);
    }

    /**
     * @param {boolean} snap
     * @param {number} [velocity]
     */
    endDrag(snap, velocity = 0) {
      if (!this._dragging && !this.el?.classList.contains('is-dragging')) return;

      this._dragging = false;
      this.el?.classList.remove('is-dragging');
      this.el?.removeEventListener('pointermove', this._onPointerMove);
      this.el?.removeEventListener('pointerup', this._onPointerUp);
      this.el?.removeEventListener('pointercancel', this._onPointerUp);

      if (this._pointerCaptured && this._pointerId != null) {
        try {
          this.el?.releasePointerCapture?.(this._pointerId);
        } catch {
          // ignore
        }
      }
      this._pointerCaptured = false;
      this._pointerId = null;

      if (snap) {
        const target = this.pickSnapTarget(this.el[this.scrollProp], velocity);
        this.animateTo(target);
      }
    }

    /**
     * @param {MouseEvent} event
     */
    onClickCapture(event) {
      if (!this._suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      this._suppressClick = false;
    }

    onScroll() {
      this.options.onScroll?.(this.el[this.scrollProp]);
    }

    onScrollEnd() {
      this.clearScrollEndTimer();
      this.cancelSettle();
      if (this._dragging || this._touchActive || this._raf) return;
      this.snapToNearest();
    }

    onTouchStart() {
      this._touchActive = true;
      this.interruptSnap();
    }

    /**
     * @param {TouchEvent} [event]
     */
    onTouchEnd(event) {
      this._touchActive = (event?.touches?.length ?? 0) > 0;
      // 触屏：不劫持滑动，仅在全部手指抬起后等惯性结束再左侧对齐
      if (this._touchActive || this._dragging) return;
      this.settleAndSnap();
    }
  }

  window.theme = window.theme || {};
  window.theme.modules = window.theme.modules || {};
  window.theme.modules.DragSnapScroll = DragSnapScroll;

  if (!customElements.get('drag-snap-scroll')) {
    customElements.define(
      'drag-snap-scroll',
      class DragSnapScrollElement extends HTMLElement {
        connectedCallback() {
          const snapSelector = this.getAttribute('data-snap-selector');
          const axis = this.getAttribute('data-axis') === 'y' ? 'y' : 'x';
          this._scroller = new DragSnapScroll(this, {
            axis,
            snapSelector,
            getSnapStep: () => {
              const stepAttr = this.getAttribute('data-snap-step');
              if (stepAttr) return Number(stepAttr) || 0;
              const stepSelector = this.getAttribute('data-snap-step-selector');
              if (!stepSelector) return 0;
              const node = this.querySelector(stepSelector);
              return this._scroller?.isHorizontal
                ? node?.offsetWidth || 0
                : node?.offsetHeight || 0;
            },
          });
          this._scroller.bind();
        }

        disconnectedCallback() {
          this._scroller?.destroy();
          this._scroller = null;
        }

        /**
         * @param {number} direction
         */
        scrollByStep(direction) {
          this._scroller?.scrollByStep(direction);
        }

        snapToNearest() {
          this._scroller?.snapToNearest();
        }
      },
    );
  }
})();
